import { useState, MouseEvent } from "react";
import "@/styles/ContextMenu.css";
import { Box, Button, IconButton, Menu, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContextMenu from "@shared/components/ContextMenu";
import { Vector2d } from "konva/lib/types";
import { ContextMenuButtons } from "@/types/types";
import { LocalCannedResponse } from "@features/annogram/sidebar/state/cannedResponsesSlice";

interface Props {
  coords: Vector2d;
  responseId: string;
  closeMenu: () => void;
  onEdit: (id: string, updatedResponse: Partial<LocalCannedResponse>) => void;
  onDelete: (id: string) => void;
  responses: LocalCannedResponse[];
}

const CannedResponseContextMenu = ({
  coords,
  responseId,
  closeMenu,
  onEdit,
  onDelete,
  responses,
}: Props) => {
  const currentResponse = responses.find((r) => r.id === responseId);

  const [editAnchor, setEditAnchor] = useState<HTMLLIElement | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(currentResponse?.title || "");
  const [editContent, setEditContent] = useState(
    currentResponse?.content || ""
  );

  const handleEditClick = (event: MouseEvent<HTMLLIElement>) => {
    setEditAnchor(event.currentTarget);
    setIsEditOpen(true);
    if (currentResponse) {
      setEditTitle(currentResponse.title);
      setEditContent(currentResponse.content);
    }
  };

  const handleResponseDelete = () => {
    onDelete(responseId);
    closeMenu();
  };

  const handleResponseEdit = () => {
    onEdit(responseId, {
      title: editTitle,
      content: editContent,
    });
    closeMenu();
  };

  const menuButtons: ContextMenuButtons = [
    { text: "Edit Response", onClick: handleEditClick },
    { text: "Delete Response", onClick: handleResponseDelete },
  ];

  return (
    <>
      <ContextMenu
        menuCoords={coords}
        menuButtons={menuButtons}
        open={true}
        onClose={closeMenu}
      />
      <Menu
        anchorEl={editAnchor}
        open={isEditOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ direction: "flex" }}
      >
        <IconButton
          sx={{ position: "absolute", top: "0px", right: "0px" }}
          onClick={closeMenu}
        >
          <CloseIcon sx={{ fontSize: "13px" }} />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "15px",
            padding: "10px",
            maxWidth: "30vw",
            minWidth: "20vw",
          }}
        >
          <TextField
            sx={{ padding: "5px", mb: 2 }}
            fullWidth
            label="Title"
            variant="outlined"
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
          <TextField
            sx={{ padding: "5px" }}
            fullWidth
            label="Content"
            variant="outlined"
            type="text"
            value={editContent}
            multiline
            rows={4}
            onChange={(e) => setEditContent(e.target.value)}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              padding: "5px",
              mt: 2,
            }}
          >
            <Button
              size="small"
              onClick={handleResponseEdit}
              variant="outlined"
              color="secondary"
              sx={{ marginRight: "2px" }}
            >
              Save
            </Button>
            <Button
              size="small"
              onClick={closeMenu}
              variant="outlined"
              color="secondary"
              sx={{ marginLeft: "2px" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default CannedResponseContextMenu;
